import type { ColumnConfig, RowState, ComputedFieldConfig } from './types';
import { deepEqual, isRowEmpty, isSeparatorRow, createEmptyRow, generateRowId, resolveColumnReadOnly } from './utils';

// ---- State ----

export interface TableState<T extends Record<string, unknown>> {
  currentData: T[];
  /** O(1) lookup: _rowId → physical array index in currentData */
  rowIdIndex: Map<string, number>;
  rowStates: Map<string, RowState>;
  changedCells: Map<string, Set<string>>;
  originalDataMap: Map<string, T>;
  undoStack: UndoGroup[];
  activeUndoGroup: UndoEntry[] | null;
}

interface UndoEntry {
  rowId: string;
  field: string;
  previousValue: unknown;
}

type UndoGroup = UndoEntry[];

// ---- Actions ----

export type TableAction<T extends Record<string, unknown>> =
  | { type: 'INIT'; initialData: T[]; rowIdField: string }
  | { type: 'CELL_CHANGE'; rowId: string; field: string; newValue: unknown }
  | { type: 'ROW_ROLLBACK'; rowId: string }
  | { type: 'ROW_DELETE'; rowId: string }
  /**
   * Add an empty row. With `separatorRowId` + `monthKey`, inserts after the
   * separator and stamps `_monthKey` on the new row (month-bucketed tables,
   * e.g. budgets). Without anchors, appends at the end of currentData (flat
   * tables).
   */
  | { type: 'ADD_ROW'; separatorRowId?: string; monthKey?: string }
  /**
   * Clear dirty tracking after `onSave` succeeds — the user's `currentData`
   * now IS the server's state, so treat it as the new baseline. Avoids the
   * visual "dirty" tint lingering between save completion and React Query
   * refetch, and prevents INIT's preserve-dirty guard from blocking the
   * subsequent refetch.
   */
  | { type: 'SAVE_COMPLETE' }
  | { type: 'UNDO' }
  | { type: 'DISCARD'; initialData: T[] }
  | { type: 'START_UNDO_GROUP' }
  | { type: 'END_UNDO_GROUP' };

// ---- Reducer context (injected, not part of state) ----

export interface ReducerContext<T extends Record<string, unknown>> {
  columns: ColumnConfig[];
  rowIdField: string;
  computedFields?: ComputedFieldConfig[];
  /** Non-column fields that participate in change detection (see `EditableTableProps.trackedFields`). */
  trackedFields?: string[];
}

/** Get the _rowId from a row */
function getRowId(row: Record<string, unknown>): string {
  return row._rowId as string;
}

/** Build a _rowId → array index map from data */
function buildRowIdIndex<T extends Record<string, unknown>>(data: T[]): Map<string, number> {
  const index = new Map<string, number>();
  for (let i = 0; i < data.length; i++) {
    index.set(getRowId(data[i]), i);
  }
  return index;
}

/** Find a row index by _rowId using the index map, with fallback linear scan */
function findByRowId<T extends Record<string, unknown>>(
  data: T[],
  rowId: string,
  indexMap?: Map<string, number>,
): number {
  if (indexMap) {
    return indexMap.get(rowId) ?? -1;
  }
  return data.findIndex(r => getRowId(r) === rowId);
}

/**
 * Build cached lookups for a data array:
 *  - allRows: filtered (non-separator) rows
 *  - dataRowIndexMap: physical index → index within allRows
 * Computed once and reused across cascaded recomputeRow calls.
 */
function buildRecomputeContext<T extends Record<string, unknown>>(data: T[]): {
  allRows: T[];
  dataRowIndexMap: number[];
} {
  const allRows: T[] = [];
  const dataRowIndexMap: number[] = new Array(data.length);
  for (let i = 0; i < data.length; i++) {
    if (isSeparatorRow(data[i])) {
      dataRowIndexMap[i] = -1;
    } else {
      dataRowIndexMap[i] = allRows.length;
      allRows.push(data[i]);
    }
  }
  return { allRows, dataRowIndexMap };
}

/** Recompute computed fields for a single row — returns a new row object (no mutation) */
function recomputeRow<T extends Record<string, unknown>>(
  data: T[],
  rowIndex: number,
  computedFields: ComputedFieldConfig[],
  context: { allRows: T[]; dataRowIndexMap: number[] },
): T {
  const row = data[rowIndex];
  if (!row || isSeparatorRow(row)) return row;

  const dataRowIndex = context.dataRowIndexMap[rowIndex];

  const current = { ...row } as T;
  for (const cf of computedFields) {
    const value = cf.compute(
      current as Record<string, unknown>,
      context.allRows as Record<string, unknown>[],
      dataRowIndex,
    );
    (current as Record<string, unknown>)[cf.field] = value;
  }

  return current;
}

/** Recompute computed fields for a changed row + related rows via recomputeRelated.
 *  Uses transitive cascade: when a related row is recomputed, its own recomputeRelated
 *  is checked, and so on until no new rows are affected. This supports chained
 *  dependencies like rollover cascades (editing month 1 → month 2 → month 3 → ...).
 *  Returns the set of all recomputed row indices (including related rows). */
function recomputeAfterChange<T extends Record<string, unknown>>(
  data: T[],
  changedRowIndex: number,
  computedFields: ComputedFieldConfig[],
  field: string,
): Set<number> {
  const recomputedIndices = new Set<number>();
  if (!computedFields.length) return recomputedIndices;

  const affectedCfs = computedFields.filter(cf => cf.dependsOn.includes(field));
  if (affectedCfs.length === 0) return recomputedIndices;

  // Build allRows / dataRowIndexMap once for this whole cascade.
  // The mutations we make to `data` below replace cells in-place but preserve
  // separator-vs-data positions, so the cached context remains valid.
  const context = buildRecomputeContext(data);

  // Recompute the changed row first
  data[changedRowIndex] = recomputeRow(data, changedRowIndex, computedFields, context);
  recomputedIndices.add(changedRowIndex);
  // Keep the cached allRows in sync with the in-place mutation
  const dri = context.dataRowIndexMap[changedRowIndex];
  if (dri !== -1) context.allRows[dri] = data[changedRowIndex];

  // Transitive cascade: BFS through related rows until stable
  let frontier = new Set<number>();

  // Seed frontier from the changed row's related indices
  for (const cf of affectedCfs) {
    if (cf.recomputeRelated) {
      const indices = cf.recomputeRelated(
        data[changedRowIndex] as Record<string, unknown>,
        data as Record<string, unknown>[],
      );
      for (const idx of indices) {
        if (idx !== changedRowIndex) frontier.add(idx);
      }
    }
  }

  // Iterate until no new rows need recomputing
  while (frontier.size > 0) {
    const nextFrontier = new Set<number>();
    for (const idx of frontier) {
      if (recomputedIndices.has(idx)) continue;

      const before = data[idx];
      data[idx] = recomputeRow(data, idx, computedFields, context);
      recomputedIndices.add(idx);
      const driIdx = context.dataRowIndexMap[idx];
      if (driIdx !== -1) context.allRows[driIdx] = data[idx];

      // Only cascade further if this row actually changed
      if (before === data[idx]) continue;

      for (const cf of computedFields) {
        if (cf.recomputeRelated) {
          const related = cf.recomputeRelated(
            data[idx] as Record<string, unknown>,
            data as Record<string, unknown>[],
          );
          for (const relIdx of related) {
            if (!recomputedIndices.has(relIdx)) nextFrontier.add(relIdx);
          }
        }
      }
    }
    frontier = nextFrontier;
  }

  return recomputedIndices;
}

/** Recalculate rowStates and changedCells for a set of affected _rowIds */
function recalcForRows<T extends Record<string, unknown>>(
  data: T[],
  affectedRowIds: Set<string>,
  state: TableState<T>,
  ctx: ReducerContext<T>,
  indexMap?: Map<string, number>,
): { rowStates: Map<string, RowState>; changedCells: Map<string, Set<string>> } {
  const nextStates = new Map(state.rowStates);
  const nextCells = new Map(state.changedCells);

  for (const rowId of affectedRowIds) {
    const rowIndex = findByRowId(data, rowId, indexMap);
    if (rowIndex === -1) continue;
    const updatedRow = data[rowIndex];
    const dbId = updatedRow[ctx.rowIdField] as string | undefined;
    const originalRow = dbId ? state.originalDataMap.get(dbId) : undefined;

    if (!originalRow) {
      if (isRowEmpty(updatedRow, ctx.columns)) {
        nextStates.delete(rowId);
        data[rowIndex] = { ...data[rowIndex], _isEmptyRow: true } as T;
      } else {
        nextStates.set(rowId, 'new');
      }
      nextCells.delete(rowId);
    } else {
      // Recalc changed cells (skip readOnly/computed columns — their values are derived)
      const cellChanges = new Set<string>();
      for (const col of ctx.columns) {
        if (resolveColumnReadOnly(col, updatedRow)) continue;
        if (!deepEqual(originalRow[col.data], updatedRow[col.data])) {
          cellChanges.add(col.data);
        }
      }
      // Tracked non-column fields (staged by a cellRenderer control) count too.
      for (const field of ctx.trackedFields ?? []) {
        if (!deepEqual(originalRow[field], updatedRow[field])) cellChanges.add(field);
      }
      if (cellChanges.size === 0) {
        nextCells.delete(rowId);
      } else {
        nextCells.set(rowId, cellChanges);
      }

      // Derive row state from changedCells — O(1) instead of O(columns)
      if (cellChanges.size > 0) {
        nextStates.set(rowId, 'modified');
      } else {
        nextStates.delete(rowId);
      }
    }
  }

  return { rowStates: nextStates, changedCells: nextCells };
}

// ---- Reducer factory ----

export function createTableReducer<T extends Record<string, unknown>>(ctx: ReducerContext<T>) {
  return function tableReducer(state: TableState<T>, action: TableAction<T>): TableState<T> {
    switch (action.type) {
      case 'INIT': {
        // Preserve in-flight edits when the incoming data's row set is
        // identical by id AND the table has dirty rows. Stops unrelated
        // refetches (window focus, sibling `['assets']` invalidations)
        // from wiping the user's uncommitted work. A genuine row-set
        // change (add / remove / first-load) still resets the baseline —
        // but dirty NEW rows (no db id, rowState 'new'/'modified') are
        // carried across the reset below, so deleting/creating a sibling
        // record mid-entry can no longer wipe unsaved user rows.
        if (state.rowStates.size > 0) {
          const incomingIds = new Set<string>();
          for (const row of action.initialData) {
            const id = row[action.rowIdField] as string | undefined;
            if (id !== undefined) incomingIds.add(id);
          }
          const existingIds = state.originalDataMap;
          let setEqual = incomingIds.size === existingIds.size;
          if (setEqual) {
            for (const id of incomingIds) {
              if (!existingIds.has(id)) {
                setEqual = false;
                break;
              }
            }
          }
          if (setEqual) return state;
        }

        // Dirty id-less rows are user work-in-progress (bulk-entry rows not
        // yet saved) — prepend them to the fresh baseline with their cell
        // edits intact. Rows with a db id are rebuilt from the server data;
        // untouched blanks (fillers / added-but-empty rows) are dropped as
        // before; the undo stack does not survive the reset. Note: rows
        // whose save just succeeded are NOT preserved — SAVE_COMPLETE clears
        // rowStates before the refetch's INIT lands, so they fail the
        // dirty-rowState test and are correctly replaced by their persisted
        // twins.
        const preservedRows: T[] = [];
        const preservedStates = new Map<string, RowState>();
        const preservedCells = new Map<string, Set<string>>();
        for (const row of state.currentData) {
          if (row._isSeparator) continue;
          const dbId = row[ctx.rowIdField] as string | undefined;
          if (dbId !== undefined) continue;
          const rowId = row._rowId as string;
          const rowState = state.rowStates.get(rowId);
          if (rowState !== 'new' && rowState !== 'modified') continue;
          preservedRows.push(row);
          preservedStates.set(rowId, rowState);
          const cells = state.changedCells.get(rowId);
          if (cells) preservedCells.set(rowId, cells);
        }

        const dataMap = new Map<string, T>();
        const data = action.initialData.map(row => {
          const id = row[action.rowIdField] as string | undefined;
          const rowId = generateRowId(id);
          const tagged = { ...row, _rowId: rowId } as T;
          if (id !== undefined) {
            dataMap.set(id, tagged);
          }
          return tagged;
        });
        const merged = preservedRows.length > 0 ? [...preservedRows, ...data] : data;
        return {
          currentData: merged,
          rowIdIndex: buildRowIdIndex(merged),
          rowStates: preservedStates,
          changedCells: preservedCells,
          originalDataMap: dataMap,
          undoStack: [],
          activeUndoGroup: null,
        };
      }

      case 'CELL_CHANGE': {
        const { rowId, field, newValue } = action;
        const rowIndex = findByRowId(state.currentData, rowId, state.rowIdIndex);
        if (rowIndex === -1) return state;
        const row = state.currentData[rowIndex];
        const previousValue = row[field];

        // Exclusive checkbox columns (radio-group semantics): checking one
        // row unchecks every other row holding `true` in the same column.
        // Collected here so the clears share the target's undo group — one
        // Ctrl+Z reverts the whole flip.
        const colConfig = ctx.columns.find((c) => c.data === field);
        const exclusiveClearIndices: number[] = [];
        if (colConfig?.exclusive && newValue === true) {
          for (let i = 0; i < state.currentData.length; i++) {
            if (i === rowIndex) continue;
            const other = state.currentData[i] as Record<string, unknown>;
            if (other._isSeparator) continue;
            if (other[field] === true) exclusiveClearIndices.push(i);
          }
        }

        // Build undo entries (target first, then any exclusive clears)
        const entries: UndoEntry[] = [{ rowId, field, previousValue }];
        for (const i of exclusiveClearIndices) {
          const otherRowId = (state.currentData[i] as Record<string, unknown>)._rowId as string;
          entries.push({ rowId: otherRowId, field, previousValue: true });
        }
        let undoStack: UndoGroup[];
        let activeUndoGroup: UndoEntry[] | null;
        if (state.activeUndoGroup) {
          activeUndoGroup = [...state.activeUndoGroup, ...entries];
          undoStack = state.undoStack;
        } else {
          activeUndoGroup = null;
          undoStack = [...state.undoStack, entries];
        }

        // Update data. Only flip `_isEmptyRow` on writes to *configured*
        // columns — underscore metadata fields (_picked_expense_label, etc.)
        // shouldn't mark a row as filled, otherwise a row containing only
        // optimistic labels would be treated as dirty-with-data on save.
        // Stamp ONLY when the row already carried `_isEmptyRow` (i.e. it
        // was created via `createEmptyRow`). Existing server rows have no
        // such flag and `dirtyRows` doesn't read it on rows with a dbId,
        // so stamping `false` on them is gratuitous noise that leaks into
        // mutation payloads → PostgREST PGRST204.
        const next = [...state.currentData];
        const isConfiguredColumn = ctx.columns.some((c) => c.data === field);
        const prevEmpty = (next[rowIndex] as Record<string, unknown>)._isEmptyRow as
          | boolean
          | undefined;
        const shouldStampEmpty = prevEmpty !== undefined;
        next[rowIndex] = {
          ...next[rowIndex],
          [field]: newValue,
          ...(shouldStampEmpty
            ? { _isEmptyRow: isConfiguredColumn ? false : prevEmpty }
            : {}),
        } as T;

        // Apply exclusive-column clears (no `_isEmptyRow` stamping: a row
        // already holding `true` is a real row, not an untouched blank row).
        for (const i of exclusiveClearIndices) {
          next[i] = { ...next[i], [field]: false } as T;
        }

        // Recompute computed fields and collect all affected row IDs
        const allAffectedRowIds = new Set([rowId]);
        for (const i of exclusiveClearIndices) {
          const clearedRowId = (next[i] as Record<string, unknown>)._rowId as string;
          if (clearedRowId) allAffectedRowIds.add(clearedRowId);
        }
        if (ctx.computedFields?.length) {
          const recomputedIndices = recomputeAfterChange(next, rowIndex, ctx.computedFields, field);
          for (const idx of recomputedIndices) {
            const recomputedRowId = (next[idx] as Record<string, unknown>)._rowId as string;
            if (recomputedRowId) allAffectedRowIds.add(recomputedRowId);
          }
        }

        // Recalc state for all affected rows (including related computed rows)
        const newState: TableState<T> = {
          ...state,
          currentData: next,
          undoStack,
          activeUndoGroup,
        };
        const { rowStates, changedCells } = recalcForRows(
          next,
          allAffectedRowIds,
          { ...newState, originalDataMap: state.originalDataMap },
          ctx,
          state.rowIdIndex,
        );

        return { ...newState, rowStates, changedCells };
      }

      case 'ROW_ROLLBACK': {
        const rowIndex = findByRowId(state.currentData, action.rowId, state.rowIdIndex);
        if (rowIndex === -1) return state;
        const row = state.currentData[rowIndex];
        const rowId = action.rowId;
        const rowState = state.rowStates.get(rowId);

        const next = [...state.currentData];
        if (rowState === 'new') {
          const emptyRow = createEmptyRow<T>(ctx.columns, ctx.rowIdField);
          (emptyRow as Record<string, unknown>)._rowId = rowId;
          next[rowIndex] = emptyRow;
        } else if (rowState === 'modified' || rowState === 'deleted') {
          const dbId = row[ctx.rowIdField] as string | undefined;
          const originalRow = dbId ? state.originalDataMap.get(dbId) : undefined;
          if (originalRow) {
            next[rowIndex] = { ...originalRow };
          }
        }

        const nextStates = new Map(state.rowStates);
        nextStates.delete(rowId);
        const nextCells = new Map(state.changedCells);
        nextCells.delete(rowId);

        return { ...state, currentData: next, rowStates: nextStates, changedCells: nextCells };
      }

      case 'ROW_DELETE': {
        const rowIndex = findByRowId(state.currentData, action.rowId, state.rowIdIndex);
        if (rowIndex === -1) return state;
        const row = state.currentData[rowIndex];
        const hasId = row[ctx.rowIdField] !== undefined;
        if (!hasId) return state;

        const rowId = action.rowId;
        const nextStates = new Map(state.rowStates);
        nextStates.set(rowId, 'deleted');
        return { ...state, rowStates: nextStates };
      }

      case 'ADD_ROW': {
        const newRow = createEmptyRow<T>(ctx.columns, ctx.rowIdField);

        // Sectioned insert (e.g. budgets): anchor after the separator row and
        // tag the new row with _monthKey so it stays in its bucket.
        if (action.separatorRowId !== undefined && action.monthKey !== undefined) {
          const separatorIndex = findByRowId(state.currentData, action.separatorRowId, state.rowIdIndex);
          if (separatorIndex === -1) return state;
          (newRow as Record<string, unknown>)._monthKey = action.monthKey;
          const next = [...state.currentData];
          next.splice(separatorIndex + 1, 0, newRow);
          return { ...state, currentData: next, rowIdIndex: buildRowIdIndex(next) };
        }

        // Flat-table: prepend at the top so the blank row is the first thing
        // the user sees. No _monthKey stamping. Empty rows are filtered out
        // of `dirtyRows` via `_isEmptyRow` until the user types a value, so
        // clicking "+" and walking away is a no-op on save.
        const next = [newRow, ...state.currentData];
        return { ...state, currentData: next, rowIdIndex: buildRowIdIndex(next) };
      }

      case 'SAVE_COMPLETE': {
        // Rebuild the server baseline from whatever is currently on screen —
        // the save succeeded, so currentData IS the truth. Clears dirty
        // tracking so the cell-changed tint drops immediately (before the
        // React Query refetch lands) and so the subsequent INIT's
        // preserve-dirty guard doesn't block the refresh.
        const dataMap = new Map<string, T>();
        for (const row of state.currentData) {
          const id = row[ctx.rowIdField] as string | undefined;
          if (id !== undefined) dataMap.set(id, row);
        }
        return {
          ...state,
          rowStates: new Map(),
          changedCells: new Map(),
          originalDataMap: dataMap,
          undoStack: [],
          activeUndoGroup: null,
        };
      }

      case 'UNDO': {
        // Pop from stack or from activeGroup
        const group = state.undoStack[state.undoStack.length - 1];
        if (!group || group.length === 0) return state;

        const next = [...state.currentData];
        const affectedRowIds = new Set<string>();

        for (let i = group.length - 1; i >= 0; i--) {
          const { rowId, field, previousValue } = group[i];
          const rowIndex = findByRowId(next, rowId, state.rowIdIndex);
          if (rowIndex === -1) continue;
          next[rowIndex] = { ...next[rowIndex], [field]: previousValue } as T;
          affectedRowIds.add(rowId);
        }

        // Mark rows as empty if all editable fields are now null/empty after undo
        for (const rowId of affectedRowIds) {
          const rowIndex = findByRowId(next, rowId, state.rowIdIndex);
          if (rowIndex === -1) continue;
          if (isRowEmpty(next[rowIndex], ctx.columns)) {
            next[rowIndex] = { ...next[rowIndex], _isEmptyRow: true } as T;
          }
        }

        // Recompute computed fields using transitive cascade (same as CELL_CHANGE)
        if (ctx.computedFields?.length) {
          for (const entry of group) {
            const rowIndex = findByRowId(next, entry.rowId, state.rowIdIndex);
            if (rowIndex === -1) continue;
            const recomputedIndices = recomputeAfterChange(next, rowIndex, ctx.computedFields, entry.field);
            for (const idx of recomputedIndices) {
              const recomputedRowId = (next[idx] as Record<string, unknown>)._rowId as string;
              if (recomputedRowId) affectedRowIds.add(recomputedRowId);
            }
          }
        }

        const newState: TableState<T> = {
          ...state,
          currentData: next,
          undoStack: state.undoStack.slice(0, -1),
        };
        const { rowStates, changedCells } = recalcForRows(next, affectedRowIds, newState, ctx, state.rowIdIndex);
        return { ...newState, rowStates, changedCells };
      }

      case 'DISCARD': {
        const dataMap = new Map<string, T>();
        const data = action.initialData.map(row => {
          const id = row[ctx.rowIdField] as string | undefined;
          const rowId = generateRowId(id);
          const tagged = { ...row, _rowId: rowId } as T;
          if (id !== undefined) {
            dataMap.set(id, tagged);
          }
          return tagged;
        });
        return {
          currentData: data,
          rowIdIndex: buildRowIdIndex(data),
          rowStates: new Map(),
          changedCells: new Map(),
          originalDataMap: dataMap,
          undoStack: [],
          activeUndoGroup: null,
        };
      }

      case 'START_UNDO_GROUP':
        return { ...state, activeUndoGroup: [] };

      case 'END_UNDO_GROUP': {
        const group = state.activeUndoGroup;
        if (!group || group.length === 0) {
          return { ...state, activeUndoGroup: null };
        }
        return {
          ...state,
          undoStack: [...state.undoStack, group],
          activeUndoGroup: null,
        };
      }

      default:
        return state;
    }
  };
}

export function createInitialState<T extends Record<string, unknown>>(): TableState<T> {
  return {
    currentData: [],
    rowIdIndex: new Map(),
    rowStates: new Map(),
    changedCells: new Map(),
    originalDataMap: new Map(),
    undoStack: [],
    activeUndoGroup: null,
  };
}
